import { useState } from 'react';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal, unmaskNumber } from '@givebutter/util';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SoftCreditLink } from 'assets/js/pages/transactions//soft-credits/SoftCreditLink';
import * as SoftCreditForm from 'assets/js/pages/transactions/soft-credits/SoftCreditForm';
import { SoftCreditFormFields } from 'assets/js/pages/transactions/soft-credits/SoftCreditFormFields';
import * as Form from 'components/form';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { sortBy } from 'lodash';
import { SoftCredit } from 'types/domain';

const TransactionEditableSoftCredits = () => {
    const { data: transaction } = useCurrentTransactionOrById();
    const [selectedSoftCredit, setSelectedSoftCredit] = useState<SoftCredit | null>(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [isShowingMore, setIsShowingMore] = useState(false);
    const showSoftCreditList = !selectedSoftCredit && !isAddingNew;
    const sortedSoftCredits = sortBy(transaction.soft_credits, 'contact.last_name');
    const shownSoftCredits = sortedSoftCredits.slice(
        0,
        isShowingMore ? sortedSoftCredits.length : 3,
    );

    return (
        <div className="flex flex-col items-start gap-1">
            <p className="text-sm font-medium">Soft credit</p>

            {selectedSoftCredit && (
                <div className="w-full">
                    <SoftCreditForm.Root
                        defaultSoftCredit={selectedSoftCredit}
                        onSaveComplete={() => setSelectedSoftCredit(null)}
                    >
                        <SoftCreditFormFields required />
                        <SoftCreditForm.Actions>
                            <SoftCreditForm.DeleteButton
                                softCreditId={selectedSoftCredit.id}
                                onDelete={() => setSelectedSoftCredit(null)}
                            />
                            <div className="ml-auto flex gap-1">
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    onClick={() => setSelectedSoftCredit(null)}
                                >
                                    Cancel
                                </Button>

                                <Form.SubmitButton variant="solid" color="primary">
                                    Save
                                </Form.SubmitButton>
                            </div>
                        </SoftCreditForm.Actions>
                    </SoftCreditForm.Root>
                </div>
            )}

            {showSoftCreditList &&
                shownSoftCredits.map(softCredit => {
                    const amount = unmaskNumber(softCredit.amount);
                    return (
                        <div className="flex items-center gap-2" key={softCredit.id}>
                            <SoftCreditLink softCredit={softCredit} />
                            {amount > 0 && (
                                <span className="font-medium">
                                    <CurrencySymbol />
                                    {maskDecimal(amount)}
                                </span>
                            )}
                            <Button
                                variant="none"
                                size="sm"
                                onClick={() => {
                                    setSelectedSoftCredit(softCredit);
                                }}
                                aria-label="Edit"
                            >
                                <FontAwesomeIcon className="text-gray-400" icon={faEdit} />
                            </Button>
                        </div>
                    );
                })}

            {isAddingNew && (
                <div className="w-full">
                    <SoftCreditForm.Root
                        onSaveComplete={() => {
                            setIsAddingNew(false);
                            setIsShowingMore(true);
                        }}
                    >
                        <SoftCreditFormFields required />
                        <SoftCreditForm.Actions className="justify-end gap-1">
                            <Button
                                variant="outline"
                                color="neutral"
                                onClick={() => setIsAddingNew(false)}
                            >
                                Cancel
                            </Button>
                            <Form.SubmitButton variant="solid" color="primary">
                                Save
                            </Form.SubmitButton>
                        </SoftCreditForm.Actions>
                    </SoftCreditForm.Root>
                </div>
            )}

            {showSoftCreditList && transaction.soft_credits.length > 3 && (
                <Button
                    variant="none"
                    onClick={() => setIsShowingMore(!isShowingMore)}
                    className="text-gray-500"
                >
                    {isShowingMore ? 'Show less' : 'Show more'}...
                </Button>
            )}

            {showSoftCreditList && (
                <Button
                    variant="none"
                    color="primary"
                    className="font-medium"
                    onClick={() => setIsAddingNew(true)}
                >
                    Credit a contact +
                </Button>
            )}
        </div>
    );
};

export { TransactionEditableSoftCredits };
